import { useTheme } from '@emotion/react';
import {
  Box,
  Grid,
  InputBase,
  MenuItem,
  Select,
  Stack,
  useMediaQuery,
} from '@mui/material';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { MessageSquare } from 'lucide-react';
import React, { useState } from 'react';
import CommentNew from '../../components/CommentNew';
import CreateCommentBoxNew from '../../components/CreateCommentBoxNew';
import ReversePagination from '../../components/ReversePagination';
import { H3, H6, H7 } from '../../components/Typography';
import FlexBox from '../../components/flexbox/FlexBox';

const StyledCard = styled(Card)(({ theme, isTablet }) => ({
  justifyContent: 'center',
  alignContent: 'flex-start',
  flexDirection: 'column',
  border: 'none',
  overflow: 'visible',
  display: 'flex',
  padding: isTablet ? '1rem 0rem 0rem 0rem' : '2rem 2rem 2rem 2rem',
  borderRadius: isTablet ? '0px' : '24px',
  boxShadow: isTablet ? 'none' : '1px 4px 6px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '1rem 0rem 0rem 0rem',
    textAlign: 'center',
  },
}));

export default function Comments({ commentData = [] }) {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectValue, setSelectValue] = useState('등록순');
  const [editingComment, setEditingComment] = useState(null);

  const handleChange = event => setSelectValue(event.target.value);

  // Handle edit comment event
  React.useEffect(() => {
    const handleEditComment = event => {
      setEditingComment(event.detail);
    };

    document.addEventListener('edit-comment', handleEditComment);
    return () =>
      document.removeEventListener('edit-comment', handleEditComment);
  }, []);

  const handleEditSubmit = editedData => {
    console.log('Comment edited:', editedData);
    // Here you would typically update the comment in your data source
    setEditingComment(null);
  };

  const handleEditCancel = () => {
    setEditingComment(null);
  };

  return (
    <StyledCard
      isTablet={isTablet}
      sx={{
        backgroundColor: '#fff',
        padding: isMobile ? '18px' : '26px',
      }}
    >
      <Box className="comment-box-container"></Box>
      {/* Comments Section */}
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{
          backgroundColor: '#ffffff',
          marginTop: '-1px',
        }}
      >
        <FlexBox
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: '100%',
            padding: isTablet ? '0.5rem 1rem' : '0rem 0rem 0.5rem 0rem',
          }}
        >
          <H3>댓글 {commentData.length}</H3>
          <Select
            value={selectValue}
            onChange={handleChange}
            MenuProps={{
              MenuListProps: { style: { padding: '0', margin: '0' } },
            }}
            input={
              <InputBase
                sx={{
                  height: 40,
                  width: 110,
                  fontSize: 14,
                  textAlign: 'left',
                  paddingLeft: '10px !important',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  borderRadius: '8px',
                  color: theme.palette.primary.mainBlue,
                  backgroundColor: '#ffffff',
                  marginBottom: 0,
                  border: '1px solid #E1E1E1',
                  '& .MuiPopover-paper': { boxShadow: 'none' },
                  '& > .MuiSelect-select': { paddingRight: '0 !important' },
                }}
              />
            }
          >
            {['등록순', '제목', '내용'].map(item => (
              <MenuItem
                key={item}
                value={item}
                sx={{
                  height: 40,
                  width: 110,
                  fontSize: 14,
                  fontWeight: 300,
                  border: '1px solid #E1E1E1',
                }}
              >
                {item}
              </MenuItem>
            ))}
          </Select>
        </FlexBox>
      </Grid>

      {/* Comment List */}
      {commentData.length > 0 ? (
        <>
          {commentData.map((item, index) => (
            <Box key={index}>
              <CommentNew item={item} />
              <CommentNew item={item} type="reply" />
            </Box>
          ))}
          {/* Pagination */}
          <Stack
            alignItems="center"
            marginY="1rem"
            sx={{ pb: isMobile ? 5 : 0 }}
          >
            <ReversePagination totalItems={100} />
          </Stack>
        </>
      ) : (
        <FlexBox
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
          gap={2}
        >
          <MessageSquare size={24} color={theme.palette.text.disabled} />
          <H6 color="text.secondary">아직 등록된 댓글이 없습니다.</H6>
          <H7 color="text.disabled">
            디자이너 채은님의 게시글에 <br />
            첫번째 댓글을 남겨보세요.
          </H7>
        </FlexBox>
      )}
      <CreateCommentBoxNew
        editMode={!!editingComment}
        editCommentData={editingComment}
        onEditSubmit={handleEditSubmit}
        onEditCancel={handleEditCancel}
      />
    </StyledCard>
  );
}
